import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Loader2 } from "lucide-react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

import HazardDetails from "./HazardDetails";
import HazardCausesConsequences from "./HazardCausesConsequences";
import HazardMitigations from "./HazardMitigations";
import HazardSFAIRPStatement from "./HazardSFAIRPStatement";

import { useToast } from "../../components/hooks/use-toast";

import { calculateRiskRating } from "../../helpers/riskHelper";

import hazardService from "../../services/hazardService";
import analyticsService from "../../services/analyticsService";

const HazardEditorBreadcrumbs = ({
  project,
  riskMatrix,
  hazard,
  setHazard,
}) => {
  const [activeBreadcrumb, setActiveBreadcrumb] = useState(0);
  const [isCreating, setIsCreating] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  const breadcrumbs = [
    "Details",
    "Causes & Consequences",
    "Mitigations",
    "SFAIRP Statement",
  ];

  const addHazard = async () => {
    setIsCreating(true);

    try {
      const editedHazard = {
        ...hazard,
        unmitigatedRisk: {
          ...hazard.unmitigatedRisk,
          rating: calculateRiskRating(
            hazard.unmitigatedRisk.probability,
            hazard.unmitigatedRisk.severity,
            riskMatrix
          ),
        },
        retainedRisk: {
          ...hazard.retainedRisk,
          rating: calculateRiskRating(
            hazard.retainedRisk.probability,
            hazard.retainedRisk.severity,
            riskMatrix
          ),
        },
      };

      const hazardId = await hazardService.createHazard(project, editedHazard);
      
      // Track hazard creation in analytics
      analyticsService.trackHazardCreation(
        hazardId, 
        project.id, 
        'manual'
      );

      navigate(-1);
    } catch (error) {
      console.error("Failed to create hazard.", error);
      
      // Track error in analytics
      analyticsService.trackError('hazard_creation_error', error.message, 'addHazard');

      toast({
        variant: "destructive",
        title: "Failed to create hazard.",
        description: error.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Card className="h-full">
        <CardHeader>
          <CardTitle>New Hazard</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="flex justify-center mb-4">
            <Breadcrumb>
              <BreadcrumbList className="text-base">
                {breadcrumbs.map((breadcrumb, index) => (
                  <div className="grid grid-flow-col items-center" key={index}>
                    <BreadcrumbItem>
                      {activeBreadcrumb === index ? (
                        <BreadcrumbPage>{breadcrumb}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink>{breadcrumb}</BreadcrumbLink>
                      )}
                    </BreadcrumbItem>

                    {index < breadcrumbs.length - 1 && (
                      <BreadcrumbSeparator className="ml-3" />
                    )}
                  </div>
                ))}{" "}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Details */}
          {activeBreadcrumb === 0 && (
            <HazardDetails
              project={project}
              hazard={hazard}
              setHazard={setHazard}
            />
          )}

          {/* Causes & Consequences */}
          {activeBreadcrumb === 1 && (
            <HazardCausesConsequences
              project={project}
              riskMatrix={riskMatrix}
              hazard={hazard}
              setHazard={setHazard}
            />
          )}

          {/* Mitigations */}
          {activeBreadcrumb === 2 && (
            <HazardMitigations
              project={project}
              riskMatrix={riskMatrix}
              hazard={hazard}
              setHazard={setHazard}
            />
          )}

          {/* SFAIRP Statement */}
          {activeBreadcrumb === 3 && (
            <HazardSFAIRPStatement
              project={project}
              hazard={hazard}
              setHazard={setHazard}
            />
          )}
        </CardContent>

        <CardFooter className="mx-auto mt-4">
          <div className="w-full">
            <div className="grid grid-cols-2">
              <div className="col-span-1">
                {activeBreadcrumb > 0 && (
                  <Button
                    className="min-w-[120px]"
                    variant="outline"
                    onClick={() => setActiveBreadcrumb(activeBreadcrumb - 1)}
                  >
                    Previous
                  </Button>
                )}
              </div>

              <div className="flex justify-end col-span-1">
                {activeBreadcrumb === breadcrumbs.length - 1 ? (
                  <Button
                    className="min-w-[120px]"
                    onClick={addHazard}
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <span>Create</span>
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    className="min-w-[120px]"
                    variant="outline"
                    onClick={() => setActiveBreadcrumb(activeBreadcrumb + 1)}
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardFooter>
      </Card>
    </>
  );
};

export default HazardEditorBreadcrumbs;
